import type { CardModel } from "../app/cards-model.js";
import { el } from "../lib/dom.js";
import { icon, type IconId } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { loadFailed, setBusy } from "./busy.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * 15 Cards (DCTA-111 run 2; UX spec §E.3/§E.4/§E.6, mock-up 15-cards.html): the card art — a flat slab
 * in the card's colour with a fine engraved pattern (inline SVG strokes, never a gradient), the last
 * four the biggest thing on it, the kind of card a word, "Frozen" in words — the status line, the
 * actions the lab can do, and a chip per card.
 *
 * Actions, by what the bank serves: Freeze and Lost or stolen (the fast path, `freeze_card` on the
 * card's account — the controller's one sheet), Show details (the bank's own reveal, the chat's
 * component), Pay the bill (the assistant, as the account's own action does), and on a frozen card
 * Talk to us — unfreezing is a signed loosening (Check and sign) the lab cannot build yet (ASK-4).
 * Not drawn (backend asks): card limits, the online/overseas/contactless controls (ASK-5), a virtual
 * card (ASK-6), replacing a card (ASK-7).
 */
export type CardAct = "freeze" | "lost" | "details" | "pay" | "talk";

export interface CardsHandlers {
  onBack(): void;
  onSelect(cardId: string): void;
  onAct(go: CardAct, cardId: string): void;
  onRetry(): void;
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
}

const SVG = "http://www.w3.org/2000/svg";

/** The engraved line pattern (15-cards.html): 22 rings from the lower right, 6 from the upper left. */
function pattern(): SVGSVGElement {
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("class", "pat");
  svg.setAttribute("viewBox", "0 0 320 202");
  svg.setAttribute("preserveAspectRatio", "xMaxYMax slice");
  svg.setAttribute("aria-hidden", "true");
  const ring = (cx: number, cy: number, r: number): void => {
    const c = document.createElementNS(SVG, "circle");
    c.setAttribute("cx", String(cx));
    c.setAttribute("cy", String(cy));
    c.setAttribute("r", String(r));
    svg.appendChild(c);
  };
  for (let i = 0; i < 22; i += 1) ring(330, 236, 46 + i * 12);
  for (let i = 0; i < 6; i += 1) ring(-30, -40, 60 + i * 16);
  return svg;
}

export class CardsView {
  readonly parts: readonly HTMLElement[];
  readonly body: HTMLElement;
  readonly back: HTMLButtonElement;
  readonly #h: CardsHandlers;
  readonly #title: HTMLElement;
  readonly #note: HTMLElement;
  readonly #done: HTMLElement;
  readonly #loud: HTMLElement;
  readonly #wrap: HTMLElement;
  readonly #detail: HTMLElement;

  constructor(handlers: CardsHandlers, opts: { readonly tab: Tab; readonly back: string }) {
    this.#h = handlers;
    this.back = el("button", { class: "raw icobtn back", type: "button", aria: { label: opts.back } }, [icon("i-chev-l")]);
    this.back.addEventListener("click", () => this.#h.onBack());
    this.#title = el("span", { class: "t" }, ["Cards"]);
    this.#note = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#done = el("div", { class: "banner done", role: "status", hidden: true });
    const retry = el("button", { class: "raw retry", type: "button" }, ["Try again"]);
    retry.addEventListener("click", () => this.#h.onRetry());
    this.#loud = el("div", { class: "banner loud", role: "alert", hidden: true }, [
      icon("i-alert"),
      el("span", { class: "bt" }, [el("b", {}, ["Your card is not frozen"]), el("span", {}, ["We couldn't reach the bank. Try again, or call us and we'll freeze it."])]),
      retry,
    ]);
    this.#wrap = el("div", { class: "cwrap" });
    this.#detail = el("div", { class: "cdetail" });
    this.body = el("div", { class: "body" }, [this.#note, this.#done, this.#loud, this.#wrap, this.#detail, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    const bar = tabBar(opts.tab, { onTab: (t) => this.#h.onTab(t), ...this.#h.assistant }, { pushed: true });
    this.parts = [el("div", { class: "topbar" }, [this.back, this.#title, el("span", { class: "topbar-pad", aria: { hidden: "true" } })]), this.body, bar.dock];
  }

  loading(): void {
    this.#busy(true, "Loading your card");
    this.#wrap.replaceChildren(el("span", { class: "sk sk-card" }), el("span", { class: "sk sk-line" }), el("span", { class: "sk sk-acts" }));
  }

  show(cards: readonly CardModel[], selected: string): void {
    // DCTA-120 (QA2-08, WCAG 2.4.3): a redraw replaces the card's actions; focus that was on one of them
    // must not fall to the page — the same action if it is still there, else the card's first action.
    const active = typeof document === "undefined" ? null : document.activeElement;
    const had = active instanceof HTMLElement && this.#wrap.contains(active) ? (active.dataset["go"] ?? "") : undefined;
    this.#busy(false, "");
    const c = cards.find((x) => x.id === selected) ?? cards[0];
    if (c === undefined) {
      this.#wrap.replaceChildren(el("p", { class: "nomatch" }, ["There's no card on this account."]));
      return;
    }
    this.#title.textContent = c.title;
    const blocks: HTMLElement[] = [];
    if (cards.length > 1) {
      blocks.push(
        el(
          "div",
          { class: "filters cardsel", role: "group", aria: { label: "Which card" } },
          cards.map((x) => {
            const chip = el("button", { class: "raw chip", type: "button", aria: { pressed: x.id === c.id ? "true" : "false", label: x.chipLabel } }, [x.chip]);
            chip.addEventListener("click", () => {
              if (x.id !== c.id) this.#h.onSelect(x.id);
            });
            return chip;
          }),
        ),
      );
    }
    blocks.push(
      el("div", { class: `cardart${c.plat ? " plat" : ""}${c.frozen ? " frozen" : ""}`, role: "img", aria: { label: c.artLabel } }, [
        pattern(),
        el("div", { class: "top" }, [
          el("span", { class: "kind" }, [el("span", { class: "k1" }, [c.k1]), el("small", { class: "k2" }, [c.k2])]),
          icon("i-wave"),
          el("span", { class: "frz" }, [icon("i-snow"), "Frozen"]),
        ]),
        el("span", { class: "emv", aria: { hidden: "true" } }),
        el("div", { class: "bot" }, [
          el("span", { class: "num money" }, [el("span", { class: "dots", aria: { hidden: "true" } }, ["••••"]), el("span", { class: "l4" }, [c.last4])]),
          el("span", { class: "hold" }, [el("span", { class: "exp" }, [c.expiry])]),
        ]),
      ]),
      el("div", { class: "cstat", role: "status" }, [el("span", {}, [el("b", {}, [c.status.strong]), c.status.rest])]),
      el("div", { class: "acts2 cacts" }, this.#acts(c)),
    );
    this.#wrap.replaceChildren(...blocks);
    if (had !== undefined) {
      const same = had === "" ? null : this.#wrap.querySelector<HTMLElement>(`.act[data-go="${CSS.escape(had)}"]`);
      (same ?? this.#wrap.querySelector<HTMLElement>(".cacts .act"))?.focus({ preventScroll: true });
    }
  }

  /** A freeze went on: the bank's own line, once, as a status. */
  done(line: string): void {
    this.#done.replaceChildren(icon("i-check"), el("span", { class: "bt" }, [line]));
    this.#done.hidden = false;
  }

  /** A freeze did not go on (§E.2: a protective failure is loud). */
  notFrozen(on: boolean): void {
    this.#loud.hidden = !on;
  }

  unreachable(): void {
    this.#busy(false, "");
    this.#wrap.replaceChildren(loadFailed("Couldn't load your cards", "Your cards and money are not affected.", () => this.#h.onRetry()));
  }

  /** The card's details block (the bank's reveal), under the actions — or none. */
  details(block: HTMLElement | undefined): void {
    this.#detail.replaceChildren(...(block === undefined ? [] : [block]));
  }

  #acts(c: CardModel): HTMLElement[] {
    const list: [CardAct, IconId, string][] = [];
    if (c.frozen) list.push(["details", "i-eye", "Show details"], ["talk", "i-phone", "Talk to us"]);
    else if (c.kind === "credit") list.push(["details", "i-eye", "Show details"], ["pay", "i-send", "Pay the bill"]);
    else {
      if (c.freezeTarget !== undefined) list.push(["freeze", "i-snow", "Freeze"]);
      list.push(["details", "i-eye", "Show details"]);
      if (c.freezeTarget !== undefined) list.push(["lost", "i-alert", "Lost or stolen"]);
    }
    return list.map(([go, ic, label]) => {
      const b = el("button", { class: "raw act", type: "button", data: { go } }, [el("i", { aria: { hidden: "true" } }, [icon(ic)]), el("span", {}, [label])]);
      b.addEventListener("click", () => this.#h.onAct(go, c.id));
      return b;
    });
  }

  #busy(on: boolean, note: string): void {
    setBusy(this.body, this.#note, this.#wrap, on, note);
  }
}
